import type { Order } from '@planner/shared';
import type { ProductDataFile } from '@planner/shared';
import { apiBaseUrl, authDisabled } from './env.js';
import { msalInstance } from './msal.js';

export interface ListOrdersResult {
  orders: Order[];
  nextCursor?: string;
}

export interface ListOrdersFilters {
  region?: string;
  status?: string;
  limit?: number;
  cursor?: string;
}

/**
 * Central 401 handling: the session is gone (expired token, revoked account),
 * so clear the cached MSAL state and send the user back to the login page
 * instead of leaving each page to render a dead error message.
 */
async function handleUnauthorized(): Promise<never> {
  if (!authDisabled) {
    await msalInstance.clearCache().catch(() => undefined);
    window.location.assign('/login');
  }
  throw new Error('Your session has expired — please sign in again');
}

async function checkedResponse(response: Response): Promise<Response> {
  if (response.status === 401) {
    return handleUnauthorized();
  }
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Request failed (${response.status})`);
  }
  return response;
}

async function request<T>(token: string, path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });
  await checkedResponse(response);
  return response.json() as Promise<T>;
}

export function listOrders(token: string, filters: ListOrdersFilters = {}) {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') {
      params.set(key, String(value));
    }
  }
  const suffix = params.size > 0 ? `?${params.toString()}` : '';
  return request<ListOrdersResult>(token, `/admin/orders${suffix}`);
}

export function getOrder(token: string, reference: string) {
  return request<{ order: Order }>(token, `/admin/orders/${encodeURIComponent(reference)}`);
}

export async function exportOrdersCsv(
  token: string,
  filters: ListOrdersFilters = {},
): Promise<Blob> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') {
      params.set(key, String(value));
    }
  }
  const suffix = params.size > 0 ? `?${params.toString()}` : '';
  const response = await fetch(`${apiBaseUrl}/admin/orders/export${suffix}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  await checkedResponse(response);
  return response.blob();
}

export function getProducts(token: string, region: string, feed: string) {
  return request<ProductDataFile>(token, `/admin/products/${region}/${feed}`);
}

export function triggerSync(token: string) {
  return request<{ triggered: boolean }>(token, '/admin/sync', { method: 'POST' });
}
